/**
 * Deterministic Suggestion Engine
 *
 * Generates scenario-based suggestions from actual repo state.
 * NO randomness. NO LLM generation. Pure mapping from scenarioId + known patterns.
 * Output must be identical for same inputs.
 */

import { ScenarioData } from './validatePlaygroundFork';

export interface ForkSuggestion {
  title: string;
  file: string;
  diff: string;
  outcome: 'ALLOW' | 'BLOCK';
  summary: string;
  newState: string[];
  nextStep: string;
  blockReasons?: string[];
  decisionState: 'korros' | 'kairos';
  filesAnalyzed: string[];
  scenarioId: string;
}

/**
 * Scenario ID to suggestion mapping.
 * Pure deterministic mapping - no randomness, no LLM.
 */
function getSuggestionForScenario(scenarioId: string): ForkSuggestion {
  switch (scenarioId) {
    case 'react-dashboard-null-crash':
      return {
        title: 'Review patch for dashboard crash on null GitHub data',
        file: 'scenarios/react-dashboard-null-crash/src/Dashboard.jsx',
        diff: `@@ -42,6 +42,12 @@ function Dashboard({ data }) {
-  const repos = data.github.repositories;
+  const repos = data?.github?.repositories || [];
+
+  if (!data?.github) {
+    console.warn("GitHub data not available — using fallback state");
+  }

   return (
     <div>
-      {repos.map(repo => (
+      {repos.length === 0 ? (
+        <div>No repositories connected yet</div>
+      ) : (
+        repos.map(repo => (
           <RepoCard key={repo.id} repo={repo} />
-      ))}
+        ))
+      )}
     </div>
   );
 }`,
        outcome: 'ALLOW',
        summary: 'This change prevents a potential crash when GitHub data is missing or undefined. Your dashboard will continue to work even if no repositories are connected.',
        newState: [
          'Dashboard no longer crashes on null GitHub data',
          'Empty state is shown instead of breaking',
          'Improved stability for first-time users'
        ],
        nextStep: 'Review the generated Dashboard null-safety patch, or copy it locally to inspect first.',
        decisionState: 'kairos',
        filesAnalyzed: [
          'scenarios/react-dashboard-null-crash/xable-scenario.json',
          'scenarios/react-dashboard-null-crash/src/Dashboard.jsx',
          'scenarios/react-dashboard-null-crash/src/Dashboard.test.js'
        ],
        scenarioId: 'react-dashboard-null-crash'
      };

    case 'api-validation-missing':
      return {
        title: 'Review patch for API validation to prevent invalid requests',
        file: 'scenarios/api-validation-missing/src/api/routes/index.ts',
        diff: `@@ -42,6 +42,12 @@ router.post("/api/proposal", async (req, res) => {
-  const { intent, action } = req.body;
+  const { intent, action } = req.body;
+
+  if (!intent || !action) {
+    return res.status(400).json({
+      error: "Missing required fields: intent and action"
+    });
+  }

   const proposal = await createProposal(intent, action);
   res.json(proposal);
 });`,
        outcome: 'ALLOW',
        summary: 'This change prevents invalid requests from reaching your agent system. Missing inputs are now rejected early with clear errors.',
        newState: [
          'Invalid API requests are blocked',
          'Clear error responses returned to clients',
          'Reduced risk of runtime failures'
        ],
        nextStep: 'Review the generated API validation patch, or copy it locally to inspect first.',
        decisionState: 'kairos',
        filesAnalyzed: [
          'scenarios/api-validation-missing/xable-scenario.json',
          'scenarios/api-validation-missing/src/api/routes/index.ts'
        ],
        scenarioId: 'api-validation-missing'
      };

    case 'async-state-race':
      return {
        title: 'Review patch for async state race condition',
        file: 'scenarios/async-state-race/src/components/AsyncComponent.tsx',
        diff: `@@ -28,6 +28,12 @@ export function AsyncComponent() {
   useEffect(() => {
     let cancelled = false;

+    const controller = new AbortController();
+
     fetchData().then(data => {
+      if (controller.signal.aborted) return;
       if (!cancelled) {
         setData(data);
       }
     });

+    return () => {
+      cancelled = true;
+      controller.abort();
+    };
   }, []);
 }`,
        outcome: 'ALLOW',
        summary: 'This change prevents stale state updates by tracking component unmount and aborting pending requests.',
        newState: [
          'No more stale state updates after unmount',
          'Requests are properly cancelled',
          'Memory leaks prevented'
        ],
        nextStep: 'Review the generated async cleanup patch, or copy it locally to inspect first.',
        decisionState: 'kairos',
        filesAnalyzed: [
          'scenarios/async-state-race/xable-scenario.json',
          'scenarios/async-state-race/src/components/AsyncComponent.tsx'
        ],
        scenarioId: 'async-state-race'
      };

    case 'full-stack-stabilization':
      return {
        title: 'Review patch for full-stack stabilization',
        file: 'scenarios/full-stack-stabilization/src/server/index.ts',
        diff: `@@ -15,6 +15,10 @@ app.post('/api/data', async (req, res) => {
   try {
     const data = await fetchData();
+    if (!data) {
+      return res.status(503).json({ error: 'Service unavailable' });
+    }
     res.json(data);
   } catch (error) {
     res.status(500).json({ error: 'Internal server error' });
   }
 });`,
        outcome: 'ALLOW',
        summary: 'This change adds proper error handling and null checks to stabilize the full-stack data flow.',
        newState: [
          'Graceful degradation on service failures',
          'Null-safe data handling',
          'Better error visibility'
        ],
        nextStep: 'Xable generated the safest available patch candidate. Review it before applying.',
        decisionState: 'kairos',
        filesAnalyzed: [
          'scenarios/full-stack-stabilization/xable-scenario.json',
          'scenarios/full-stack-stabilization/src/server/index.ts',
          'scenarios/full-stack-stabilization/src/client/App.tsx'
        ],
        scenarioId: 'full-stack-stabilization'
      };

    case 'governed-workflow-simulation':
      return {
        title: 'Review patch for governed workflow simulation',
        file: 'scenarios/governed-workflow-simulation/src/workflow/executor.ts',
        diff: `@@ -42,6 +42,10 @@ export async function executeWorkflow(workflowId: string) {
   const workflow = await getWorkflow(workflowId);
   
   if (!workflow) {
+    throw new Error(\`Workflow not found: \${workflowId}\`);
+  }
+
+  if (workflow.status !== 'ready') {
+    throw new Error(\`Workflow not ready: \${workflow.status}\`);
   }
   
   return await runSteps(workflow.steps);
 }`,
        outcome: 'ALLOW',
        summary: 'This change adds pre-execution validation to ensure workflows are in the correct state before execution.',
        newState: [
          'Workflows validated before execution',
          'Clear error messages for invalid states',
          'Prevents invalid workflow execution'
        ],
        nextStep: 'The patch is safe to apply. Review and apply it when ready.',
        decisionState: 'kairos',
        filesAnalyzed: [
          'scenarios/governed-workflow-simulation/xable-scenario.json',
          'scenarios/governed-workflow-simulation/src/workflow/executor.ts'
        ],
        scenarioId: 'governed-workflow-simulation'
      };

    case 'korros-governed-agent-challenge':
      return {
        title: 'Review patch for Korros governed agent challenge',
        file: 'scenarios/korros-governed-agent-challenge/src/agent/controller.ts',
        diff: `@@ -28,6 +28,10 @@ export class AgentController {
     if (!this.authority.hasPermission('execute_action')) {
       throw new Error('Permission denied');
     }
+    
+    if (!this.policy.isActionAllowed(action)) {
+      throw new Error('Action not allowed by policy');
+    }
     
     return await this.executor.run(action);
   }`,
        outcome: 'ALLOW',
        summary: 'This change demonstrates Korros governance by adding policy checks before action execution.',
        newState: [
          'Policy validation before execution',
          'Authority checks enforced',
          'Governed agent behavior'
        ],
        nextStep: 'Xable generated the safest available patch candidate. Review it before applying.',
        decisionState: 'korros',
        filesAnalyzed: [
          'scenarios/korros-governed-agent-challenge/xable-scenario.json',
          'scenarios/korros-governed-agent-challenge/src/agent/controller.ts'
        ],
        scenarioId: 'korros-governed-agent-challenge'
      };

    default:
      // Fallback for unknown scenarios
      return {
        title: 'Review generated patch',
        file: 'unknown',
        diff: '',
        outcome: 'BLOCK',
        summary: 'Scenario not recognized. Please select a valid scenario.',
        newState: ['No changes'],
        nextStep: 'Select a valid scenario from the list.',
        decisionState: 'korros',
        filesAnalyzed: [],
        scenarioId: scenarioId
      };
  }
}

/**
 * Generate deterministic suggestion from fork data and scenario ID.
 * 
 * Rules:
 * - NO randomness
 * - NO LLM generation
 * - Pure mapping from scenarioId + known patterns
 * - Output must be identical for same inputs
 * 
 * @param forkData - Scenario data from fork validation
 * @param scenarioId - Selected scenario identifier
 * @returns Deterministic suggestion
 */
export function generateSuggestionFromFork(
  forkData: ScenarioData,
  scenarioId: string
): ForkSuggestion {
  const baseSuggestion = getSuggestionForScenario(scenarioId);
  
  // Override filesAnalyzed with actual files read from fork
  const filesAnalyzed = forkData.files.map(f => f.path);
  
  return {
    ...baseSuggestion,
    filesAnalyzed
  };
}

/**
 * Determine decision state (Korros/Kairos) based on test state.
 * 
 * Simple deterministic rule:
 * - If tests failing → Korros (blocked)
 * - If tests passing after patch simulation → Kairos (safe)
 * 
 * For this implementation, we use the scenario's inherent state:
 * - korros-governed-agent-challenge → Korros
 * - All others → Kairos (after patch)
 */
export function determineDecisionState(scenarioId: string): 'korros' | 'kairos' {
  if (scenarioId === 'korros-governed-agent-challenge') {
    return 'korros';
  }
  return 'kairos';
}
